import { mkdirSync, writeFileSync } from 'fs';
import { join } from 'path';
import type { IFilesystem } from '@livx.cc/wcli/core';
import type { ChatLike, Message } from './llm';
import type { AgentTool, ToolContext } from './tools';
import { toolsByName } from './tools';
import type { AgentDef } from './agents';
import type { Hooks } from './hooks';
import { Agent, type RunResult } from './Agent';
import { OverlayFilesystem } from './OverlayFilesystem';
import { findGitRoot, getOrCreateWorktree, cleanupWorktree } from './worktree';
import { NodeDiskFilesystem } from './NodeDiskFilesystem';
import { forComponent } from './logging';

const log = forComponent('subagent');

/** Run items through `fn` with at most `limit` in flight, preserving result order. */
async function boundedPool<T, R>(items: T[], limit: number, fn: (item: T, i: number) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async () => { while (next < items.length) { const i = next++; out[i] = await fn(items[i], i); } };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return out;
}

export interface TaskToolOptions {
  /** The same ai.libx.js client the parent uses — the child shares it. */
  ai: ChatLike;
  model: string;
  /** The filesystem the child operates over (jail/scope it before passing, if desired). */
  fs: IFilesystem;
  /** Step budget for the child run (independent of the parent's). */
  maxSteps?: number;
  /** Recursion depth of the child this tool would spawn (0 = first level). */
  depth?: number;
  /** Hard ceiling on nesting — beyond this the tool refuses to spawn. */
  maxDepth?: number;
  /** Named subagent types selectable via the `agentType` param (persona + model + scoped tools). */
  agents?: AgentDef[];
  /** Max children running concurrently in a `TaskBatch` (default 4). */
  maxParallel?: number;
  /** Parent hooks — `onSubagentStop` fires with each child's summary when it finishes. */
  hooks?: Hooks;
  /** Origin-anchored real-disk dir for child transcript traces (see `AgentOptions.traceDir`). Passed
   *  down UNCHANGED to nested children so every level writes to the same origin `.agent`. */
  traceDir?: string;
}

type WorktreeHandle = { repoRoot: string; slug: string; worktreePath: string; branch: string };

function sanitizeSlug(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9._-]/g, '-').replace(/-+/g, '-').slice(0, 40);
}

function createWorktreeFs(cwd: string, label: string, index: number): WorktreeHandle | string {
  const repoRoot = findGitRoot(cwd);
  if (!repoRoot) return 'isolation: "worktree" requires a git repository';
  const slug = `sub-${index}-${sanitizeSlug(label)}`;
  try {
    const info = getOrCreateWorktree(repoRoot, slug);
    return { repoRoot, slug, worktreePath: info.worktreePath, branch: info.branch };
  } catch (e) { return `worktree creation failed: ${e instanceof Error ? e.message : String(e)}`; }
}

function releaseWorktree(handle: WorktreeHandle): { removed: boolean; reason?: string } {
  return cleanupWorktree(handle.repoRoot, handle.slug, { force: true });
}

function worktreePromptPrefix(branch: string): string {
  return `You are running in an isolated git worktree on branch "${branch}". ` +
    'When you finish making changes, commit them with a clear message before replying with your summary. ' +
    'Your worktree will be removed after you finish — only committed work survives (on the branch).\n\n';
}

// ── Subagent tracing ──────────────────────────────────────────────────────────────────────────────
// A child's transcript is otherwise ephemeral (only its final summary returns to the parent). We stream
// it to a real-disk file under the ORIGIN's trace dir — bypassing the child's (possibly overlay/worktree)
// fs on purpose, so the trace survives interruption AND worktree teardown. On a non-`stop` finish the tool
// result becomes a STATUS HINT pointing here, instead of a dangling tool_call.

interface TraceMeta { label: string; agentType?: string; prompt: string }

let traceSeq = 0; // process-unique → no same-millisecond collisions across concurrent TaskBatch children

function traceFileFor(dir: string, label: string, depth: number): string | undefined {
  try {
    mkdirSync(dir, { recursive: true });
    const id = `${Date.now().toString(36)}-${(traceSeq++).toString(36)}`;
    return join(dir, `${id}-d${depth}-${sanitizeSlug(label) || 'sub'}.json`);
  } catch (e) {
    log.warn(`subagent trace dir unavailable (${dir}) — running without a trace: ${e instanceof Error ? e.message : String(e)}`);
    return undefined; // tracing is best-effort infra; never block a delegation on it
  }
}

function flushTrace(file: string, meta: TraceMeta, messages: Message[], status: string): void {
  try {
    writeFileSync(file, JSON.stringify({ ...meta, status, updated: Date.now(), messages }, null, 2));
  } catch (e) {
    log.warn(`subagent trace flush failed (${file}): ${e instanceof Error ? e.message : String(e)}`);
  }
}

/** Wrap the child's host so each step boundary (`turn_start`) flushes the committed-so-far transcript. */
function installTrace(child: Agent, file: string, meta: TraceMeta): void {
  const prev = child.options.host;
  child.options.host = {
    ...prev,
    notify: (e) => {
      if (e.kind === 'turn_start') flushTrace(file, meta, child.transcript, 'running');
      prev?.notify?.(e);
    },
  };
}

/** The tool result for a child that did NOT finish cleanly: status + what it got through + trace pointer
 *  + an explicit decision prompt. Doubles as the back-fill that keeps the parent transcript valid (no
 *  orphan tool_call) when an in-flight child is aborted. */
function abortHint(label: string, res: RunResult, file?: string): string {
  const last = (res.text || '').trim().slice(0, 500);
  const lines = [
    `[sub-task '${label}' did NOT finish — ${res.finishReason} after ${res.steps} step(s). Treat its work as INCOMPLETE.]`,
    last ? `Progress so far: ${last}` : 'It produced no summary before stopping.',
  ];
  if (file) lines.push(`Full transcript (every step it took): ${file} — Read it to see what was actually done.`);
  lines.push("Decide: ignore it, re-delegate a fresh Task that continues from the trace, or start over. Do NOT assume the sub-task completed.");
  return lines.join('\n');
}

/** Run one child to completion with tracing + (on a non-clean finish) a status hint. The child inherits
 *  the parent's abort `signal`, so Esc/Ctrl-C actually interrupts it — and the partial trace + hint are
 *  what the parent picks up. `onSubagentStop` fires with whatever result (summary OR hint) the parent gets. */
async function runChildTraced(args: {
  opts: TaskToolOptions; childOpts: NonNullable<ConstructorParameters<typeof Agent>[0]>;
  prompt: string; label: string; agentType?: string; childDepth: number; signal?: AbortSignal;
}): Promise<{ res: RunResult; result: string }> {
  const { opts, label, agentType, prompt } = args;
  if (args.signal) args.childOpts.signal = args.signal; // propagate cancellation → child stops with the parent
  const file = opts.traceDir ? traceFileFor(opts.traceDir, label, args.childDepth) : undefined;
  const meta: TraceMeta = { label, agentType, prompt };
  const child = new Agent(args.childOpts);
  if (file) installTrace(child, file, meta);
  const res = await child.run(prompt);
  if (file) flushTrace(file, meta, child.transcript, res.finishReason);
  const result = res.finishReason === 'stop'
    ? (res.text || `(child '${label}' finished with no summary; finishReason=${res.finishReason})`)
    : abortHint(label, res, file);
  await opts.hooks?.onSubagentStop?.(result, { label, agentType });
  return { res, result };
}

/** Resolve the constructor opts for one child agent (persona/model/tool scoping). Returns an error string on bad agentType. */
function childOptionsFor(opts: TaskToolOptions, fs: IFilesystem, depth: number, maxDepth: number, agentType?: string): NonNullable<ConstructorParameters<typeof Agent>[0]> | string {
  let def: AgentDef | undefined;
  if (agentType) {
    def = (opts.agents ?? []).find((a) => a.name === agentType);
    if (!def) return `no subagent type '${agentType}'. Available: ${(opts.agents ?? []).map((a) => a.name).join(', ') || '(none defined)'}`;
  }
  const childOpts: ConstructorParameters<typeof Agent>[0] = { ai: opts.ai, fs, model: def?.model ?? opts.model, subagents: true, depth: depth + 1, maxDepth, traceDir: opts.traceDir };
  if (opts.maxSteps != null) childOpts.maxSteps = opts.maxSteps;
  if (def?.systemPrompt) childOpts.systemPrompt = def.systemPrompt;
  if (def?.tools?.length) {
    try { childOpts.tools = toolsByName(def.tools); }
    catch (e) { return `subagent '${agentType}' declares an unknown tool — ${(e as Error).message}`; }
  }
  return childOpts;
}

/**
 * `Task` tool — spawn a CHILD agent over the same VFS with its own step budget,
 * returning only its final summary to the parent. Use to fan out a self-contained
 * sub-task (search, refactor a subtree) without polluting the parent's context.
 * @returns the child's final text, or an error string if the depth limit is reached.
 */
export function makeTaskTool(opts: TaskToolOptions): AgentTool {
  const depth = opts.depth ?? 0;
  const maxDepth = opts.maxDepth ?? 2;
  return {
    name: 'Task',
    description:
      'Delegate a self-contained sub-task to a child agent over the same filesystem. It runs autonomously with its own step budget and returns a concise summary — use to isolate context-heavy work (broad search, a scoped refactor). Provide a short `description` and a full `prompt`. If it is interrupted or fails, the result is a STATUS HINT with a pointer to its full transcript (Read it to recover what it did, then decide whether to continue or restart) — do not assume an incomplete sub-task succeeded. Set `background: true` to run it detached (returns a job id to poll with JobOutput while you keep working); its file edits are overlay-isolated and commit when it finishes. Set `isolation: "worktree"` to run in a real git worktree (needed when the child uses real shell commands that must see/modify actual files) — slower (~200ms setup), auto-cleaned if no changes.',
    parameters: {
      type: 'object',
      required: ['description', 'prompt'],
      properties: {
        description: { type: 'string', description: 'a short (3-5 word) label for the sub-task' },
        prompt: { type: 'string', description: 'the full instructions the child agent should carry out' },
        agentType: { type: 'string', description: 'optional named subagent type (its persona, model, and scoped tools) — see the catalog' },
        background: { type: 'boolean', description: 'run detached (overlay-isolated, commits on finish); returns a job id to poll with JobOutput instead of blocking' },
        isolation: { type: 'string', enum: ['overlay', 'worktree'], description: 'isolation mode: "overlay" (default, VFS layer) or "worktree" (real git worktree for shell-heavy work)' },
      },
    },
    async run({ description, prompt, agentType, background, isolation }, ctx: ToolContext) {
      if (depth >= maxDepth) {
        return `Error: Task depth limit reached (maxDepth ${maxDepth}). Cannot spawn another child agent — do this work directly instead.`;
      }
      const label = String(description ?? agentType ?? 'sub-task');

      const useWorktree = isolation === 'worktree';

      // Background: run the child detached over an isolated overlay via the job registry. This is where
      // sandbox backgrounding actually pays off — a child does real model calls, so it overlaps the parent.
      if (background && ctx.jobs) {
        const id = ctx.jobs.start(async ({ signal }) => {
          let fs: IFilesystem; let commit: () => Promise<void>; let wtHandle: WorktreeHandle | undefined;
          if (useWorktree) {
            const h = createWorktreeFs(process.cwd(), label, 0);
            if (typeof h === 'string') throw new Error(h);
            wtHandle = h; fs = new NodeDiskFilesystem(h.worktreePath); commit = async () => {};
          } else {
            const overlay = new OverlayFilesystem(opts.fs);
            fs = overlay; commit = () => overlay.commit();
          }
          const childOpts = childOptionsFor(opts, fs, depth, maxDepth, agentType);
          if (typeof childOpts === 'string') throw new Error(childOpts);
          const childPrompt = wtHandle ? worktreePromptPrefix(wtHandle.branch) + String(prompt ?? '') : String(prompt ?? '');
          const { res, result } = await runChildTraced({ opts, childOpts, prompt: childPrompt, label, agentType, childDepth: depth + 1, signal });
          // Aborted before commit → don't merge partial edits, but DO return the hint+trace (the parent
          // can read what it did and decide), instead of the old opaque '[killed before commit]'.
          if (res.finishReason === 'aborted' || signal.aborted) { wtHandle && releaseWorktree(wtHandle); return result; }
          await commit();
          let summary = result;
          if (wtHandle) {
            const r = releaseWorktree(wtHandle);
            if (!r.removed) summary += `\n\n[worktree removal failed: ${r.reason}]`;
          }
          return summary;
        }, { kind: 'agent', label });
        return `Started background sub-task ${id} ('${label}') — poll with JobOutput({id:"${id}"}).`;
      }

      // Foreground: the child runs inline. Worktree isolation gives it a real disk fs; overlay keeps VFS.
      let fs: IFilesystem; let wtHandle: WorktreeHandle | undefined;
      if (useWorktree) {
        const h = createWorktreeFs(process.cwd(), label, 0);
        if (typeof h === 'string') return `Error: ${h}`;
        wtHandle = h; fs = new NodeDiskFilesystem(h.worktreePath);
      } else {
        fs = opts.fs;
      }
      const childOpts = childOptionsFor(opts, fs, depth, maxDepth, agentType);
      if (typeof childOpts === 'string') { wtHandle && releaseWorktree(wtHandle); return `Error: ${childOpts}`; }
      try {
        const childPrompt = wtHandle ? worktreePromptPrefix(wtHandle.branch) + String(prompt ?? '') : String(prompt ?? '');
        // ctx.signal carries the parent's Esc/Ctrl-C → the child now actually stops with the parent
        // (it previously ran to completion, ignoring the interrupt). On stop we get the trace + hint.
        const { result } = await runChildTraced({ opts, childOpts, prompt: childPrompt, label, agentType, childDepth: depth + 1, signal: ctx.signal });
        let summary = result;
        if (wtHandle) {
          const r = releaseWorktree(wtHandle);
          if (!r.removed) summary += `\n\n[worktree removal failed: ${r.reason}]`;
        }
        return summary;
      } catch (e) {
        if (wtHandle) releaseWorktree(wtHandle);
        throw e;
      }
    },
  };
}

/**
 * `TaskBatch` tool — fan out several self-contained sub-tasks to child agents that run
 * **concurrently** (bounded pool), returning all their summaries together. Each child runs over its
 * OWN `OverlayFilesystem` (write-isolated from siblings), and successful children's edits are
 * committed **in array order** afterward — so concurrent writes resolve deterministically
 * (last index wins) instead of racing. Use to parallelize independent work (review N files, search M
 * subtrees, scoped refactors). For a single sub-task, use `Task`.
 */
export function makeTaskBatchTool(opts: TaskToolOptions): AgentTool {
  const depth = opts.depth ?? 0;
  const maxDepth = opts.maxDepth ?? 2;
  const maxParallel = opts.maxParallel ?? 4;
  return {
    name: 'TaskBatch',
    description:
      'Delegate SEVERAL independent sub-tasks to child agents that run concurrently; returns all their summaries. Each child is write-isolated (its file edits are merged back in array order). Use for parallel fan-out (review/search/scoped refactors across files). Provide `tasks: [{ description, prompt, agentType? }]`. Set `isolation: "worktree"` to run each child in its own git worktree (needed for real shell commands) — slower, auto-cleaned if no changes.',
    parameters: {
      type: 'object',
      required: ['tasks'],
      properties: {
        tasks: {
          type: 'array',
          description: 'the sub-tasks to run in parallel',
          items: {
            type: 'object',
            required: ['description', 'prompt'],
            properties: {
              description: { type: 'string', description: 'a short (3-5 word) label' },
              prompt: { type: 'string', description: 'the full instructions for this child' },
              agentType: { type: 'string', description: 'optional named subagent type' },
            },
          },
        },
        isolation: { type: 'string', enum: ['overlay', 'worktree'], description: 'isolation mode for ALL children: "overlay" (default) or "worktree" (real git worktree each)' },
      },
    },
    async run({ tasks, isolation }, ctx: ToolContext) {
      if (depth >= maxDepth) return `Error: Task depth limit reached (maxDepth ${maxDepth}). Cannot spawn child agents — do this work directly instead.`;
      const list = Array.isArray(tasks) ? tasks : [];
      if (!list.length) return 'Error: TaskBatch needs a non-empty `tasks` array.';
      const useWorktree = isolation === 'worktree';

      type Outcome = { label: string; text?: string; error?: string; overlay?: OverlayFilesystem; wtHandle?: WorktreeHandle; ok: boolean };
      const results = await boundedPool<any, Outcome>(list, maxParallel, async (t, i) => {
        const label = String(t?.description ?? t?.agentType ?? `task ${i + 1}`);
        let fs: IFilesystem; let overlay: OverlayFilesystem | undefined; let wtHandle: WorktreeHandle | undefined;
        if (useWorktree) {
          const h = createWorktreeFs(process.cwd(), label, i);
          if (typeof h === 'string') return { label, error: h, ok: false };
          wtHandle = h; fs = new NodeDiskFilesystem(h.worktreePath);
        } else {
          overlay = new OverlayFilesystem(opts.fs);
          fs = overlay;
        }
        const childOpts = childOptionsFor(opts, fs, depth, maxDepth, t?.agentType);
        if (typeof childOpts === 'string') return { label, error: childOpts, ok: false };
        try {
          const childPrompt = wtHandle ? worktreePromptPrefix(wtHandle.branch) + String(t?.prompt ?? '') : String(t?.prompt ?? '');
          const { res, result } = await runChildTraced({ opts, childOpts, prompt: childPrompt, label, agentType: t?.agentType, childDepth: depth + 1, signal: ctx.signal });
          // Merge edits for natural completions (incl. max_steps/budget), but NOT a user-cancelled child —
          // signal propagation now makes 'aborted' reachable here; merging its half-done work would be wrong
          // (mirrors the single-Task background path, which skips commit on abort).
          return { label, text: result, overlay, wtHandle, ok: res.finishReason !== 'error' && res.finishReason !== 'aborted' };
        } catch (e) { return { label, error: e instanceof Error ? e.message : String(e), wtHandle, ok: false }; }
      });

      // Overlay mode: merge successful children's edits in array order (deterministic last-writer-wins).
      // Worktree mode: force-remove each worktree (committed work survives on the branch).
      const lines: string[] = [];
      for (const r of results) {
        if (r.ok && r.overlay) await r.overlay.commit().catch(() => {});
        let extra = '';
        if (r.wtHandle) {
          const wr = releaseWorktree(r.wtHandle);
          if (!wr.removed) extra = `\n[worktree removal failed: ${wr.reason}]`;
        }
        lines.push(`### ${lines.length + 1}. ${r.label}\n${r.error ? `ERROR: ${r.error}` : (r.text || '(no summary)')}${extra}`);
      }

      return lines.join('\n\n');
    },
  };
}
